import { FC, FormEvent, useCallback, useState } from 'react';

import { Loader, SearchableSelect } from 'components';
import { Popover } from 'components/organization/popover/popover';
import { useLookups } from 'contexts';
import { useUnitApi } from 'hooks/use-unit-api';
import { ActiveUnit, UnitStatus } from 'types';

import './update-unit-status.scss';

interface UpdateUnitStatusProps extends Partial<ActiveUnit> {
  loadUnit: () => void;
}

const UpdateUnitStatus: FC<UpdateUnitStatusProps> = ({ loadUnit, ...unit }) => {
  const { updateUnitStatus } = useUnitApi();
  const { unitStatusOptions } = useLookups();

  const [loading, setLoading] = useState<boolean>(false);
  const [unitStatus, setUnitStatus] = useState<UnitStatus | null | undefined>(unit.unitStatus);
  const [statusReason, setStatusReason] = useState<string | null | undefined>(unit.statusReason);
  const [resendEDI, setResendEDI] = useState<boolean>(true);

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (!unit.id || !unitStatus) return;
      setLoading(true);
      const response = await updateUnitStatus(unit.id, resendEDI, statusReason, unitStatus);
      if (response) loadUnit();
      setLoading(false);
    },
    [unit.id, unitStatus, resendEDI, statusReason, loadUnit, setLoading, updateUnitStatus]
  );

  return (
    <Popover key={unit.id} triggerClassNames="btn-outline" trigger="SET STATUS" data-cy="unit-status-popover">
      <Loader loading={loading}>
        <form id="unit-status-form" className="grid-2" style={{ minWidth: 300 }} onSubmit={handleSubmit}>
          <SearchableSelect
            required
            containerClass="col-span-2 text-field"
            dropdownId="unit-status-options"
            id="unit-status-select"
            label="UNIT STATUS"
            onValueSelected={unitStatus => setUnitStatus(unitStatusOptions.find(value => value === unitStatus))}
            options={unitStatusOptions.map(value => ({ value, label: value }))}
            value={unitStatus ?? ''}
            data-cy="unit-status-input"
          />
          <div className="col-span-2 text-field">
            <input
              className="w-100"
              id="status-reason-input"
              type="text"
              onChange={e => setStatusReason(e.target.value)}
              value={statusReason ?? ''}
              data-cy="unit-status-reason-input"
            />
            <label htmlFor="status-reason-input">STATUS NOTES</label>
          </div>
          <div className="text-field">
            <input
              id="resend-edi-checkbox"
              type="checkbox"
              checked={resendEDI}
              onChange={e => setResendEDI(e.target.checked)}
              style={{ margin: '8px 16px 0', outline: 'none', width: 16 }}
              data-cy="unit-status-edi-checkbox"
            />
            <label htmlFor="resend-edi-checkbox">RESEND EDI</label>
          </div>
          <button className="btn-primary" type="submit" disabled={!unitStatus || unitStatus === unit.unitStatus}>
            SUBMIT
          </button>
        </form>
      </Loader>
    </Popover>
  );
};

export default UpdateUnitStatus;
